import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Copyright } from '@/components/molecules/copyright'
import { useHistory, useLocation } from 'react-router-dom'
import logo from '@/static/images/icon/logo.svg'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import dayjs, { Dayjs } from 'dayjs'
import { pmsAuthMfaChallenge } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { reportError } from '@/components/apps/error-boundary'
import { AuthContext } from '@/contexts/auth'

export const MfaAnnounce: React.FC<{}> = () => {
  const history = useHistory()
  const location = useLocation<{ idToken: string }>()
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setAuth } = useContext<any>(AuthContext)

  const [deadlineDate, setDeadlineDate] = useState<Dayjs>()

  const remainDays = useMemo(() => {
    if (!deadlineDate) {
      return 0
    }
    return Math.max(0, deadlineDate.startOf('day').diff(dayjs().startOf('day'), 'day'))
  }, [deadlineDate])

  useEffect(() => {
    if (!location.state?.idToken) {
      history.replace({ pathname: '/' })
      return
    }
    pmsAuthMfaChallenge(location.state.idToken)
      .then(res => {
        if (!res.mfaRequired) {
          setAuth(location.state)
          history.replace({ pathname: '/dashboard' })
          return
        }
        if (res.mfaSetupRequired) {
          onNext()
          return
        }
        setDeadlineDate(dayjs(res.mfaSetupDeadline))
      })
      .catch(e => {
        reportError(e)
        errorHandler(e)
      })
  }, [])

  const onSkip = () => {
    setAuth(location.state)
  }

  const onNext = () => {
    history.replace({ pathname: '/auth/otp', state: location.state })
  }

  if (!deadlineDate) {
    return <></>
  }

  return (
    <div css={pageStyle}>
      <div css={contentStyle}>
        <img alt="aipass logo" src={logo} css={logoStyle} />

        <section css={cardStyle}>
          <div css={graceBadgeStyle}>⚠ {t('移行期間中')}</div>

          <h1 css={titleStyle}>{t('メール二段階認証の設定が必要です')}</h1>

          <p css={leadStyle}>{t('セキュリティ強化のため、メール二段階認証（MFA）の設定が必要になりました。')}</p>

          <div css={deadlineBoxStyle}>
            <p css={deadlineLabelStyle}>{t('移行期限')}</p>
            <p css={deadlineDateStyle}>
              {deadlineDate.format(t('YYYY-MM-DD'))}
              <span css={remainTextStyle}>{t('（残り{{days}}日）', { days: remainDays })}</span>
            </p>
            <p css={deadlineNoteStyle}>{t('期限を過ぎると、次回ログイン時にメール二段階認証が必要です。')}</p>
          </div>

          <div css={aboutBoxStyle}>
            <p css={aboutTitleStyle}>{t('メール二段階認証とは')}</p>
            <ul css={aboutListStyle}>
              <li>{t('ログイン時にメールで確認コードを送付')}</li>
              <li>{t('パスワード漏洩時の不正アクセスを防止')}</li>
              <li>{t('一度認証すると、同じブラウザでは90日間、再認証不要です')}</li>
            </ul>
          </div>

          <Button
            buttonType={ButtonType.Primary}
            width={275}
            height={38}
            marginTop={22}
            marginLeft={'auto'}
            marginRight={'auto'}
            onClick={onNext}
          >
            {t('今すぐ設定する')}
          </Button>
        </section>

        <button css={skipLinkStyle} onClick={onSkip}>
          {t('スキップする')}
        </button>
      </div>

      <div css={footerStyle}>
        <Copyright style={{ position: 'relative', marginTop: 0, bottom: 0 }} />
      </div>
    </div>
  )
}

const baseTextStyle = css({
  fontSize: 12,
  color: '#272727',
  lineHeight: 1.5,
})

const pageStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: '100vh',
  margin: '0 auto',
  padding: '24px 12px 32px',
  backgroundImage: `url(${require('@/static/images/icon/background-pc.png')})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
})

const contentStyle = css({
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})

const logoStyle = css({
  width: 210,
  maxWidth: '70vw',
  marginBottom: 24,
})

const cardStyle = css({
  position: 'relative',
  width: 360,
  maxWidth: '92vw',
  background: '#FFFFFF',
  borderRadius: '96px 96px 88px 88px',
  padding: '34px 28px 30px',
  textAlign: 'center',
  boxShadow: '0 14px 30px rgba(0, 0, 0, 0.2)',
})

const graceBadgeStyle = css(baseTextStyle, {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 14px',
  borderRadius: 999,
  color: '#f2a40b',
  background: '#fffbf0',
  fontWeight: 700,
})

const titleStyle = css(baseTextStyle, {
  margin: '18px 0 0',
  fontSize: 18,
  fontWeight: 700,
})

const leadStyle = css(baseTextStyle, {
  margin: '16px 0 0',
  color: '#676767',
})

const deadlineBoxStyle = css({
  marginTop: 20,
  border: '2px solid #f2a40b',
  borderRadius: 36,
  textAlign: 'left',
  padding: '16px 18px',
  background: '#fffbf0',
})

const deadlineLabelStyle = css(baseTextStyle, {
  margin: 0,
  color: '#676767',
})

const deadlineDateStyle = css(baseTextStyle, {
  margin: '4px 0 0',
  fontWeight: 700,
})

const remainTextStyle = css(baseTextStyle, {
  color: '#F2A40B',
  marginLeft: 8,
  fontWeight: 700,
})

const deadlineNoteStyle = css(baseTextStyle, {
  margin: '6px 0 0',
  color: '#676767',
})

const aboutBoxStyle = css({
  marginTop: 20,
  borderRadius: 36,
  background: '#F4F7FC',
  textAlign: 'left',
  padding: '16px 18px',
})

const aboutTitleStyle = css(baseTextStyle, {
  margin: 0,
  fontWeight: 700,
})

const aboutListStyle = css(baseTextStyle, {
  margin: '8px 0 0',
  paddingLeft: 18,
  color: '#676767',
  lineHeight: 1.8,
  li: { listStyle: 'disc' },
})

const skipLinkStyle = css(baseTextStyle, {
  marginTop: 20,
  border: 'none',
  background: 'transparent',
  color: '#676767',
  cursor: 'pointer',
  '&:hover': { opacity: 0.8 },
})

const footerStyle = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
})
